import { ModalComponent } from '../../components/modal/modal'
import { type Nullable } from '../../types/common'
import { PopupStorage } from './popup-storage'
import { PopupType, type PopupVariant } from './types'
import { ConditionRuntime } from './condition-runtime'
import { POPUP_ANIMATION_DELAY, gradientOpacify } from './utils'
import { POPUP_CLASSNAMES } from './constants'
import { isMobile } from '../../utils/check-media'
import { $el, whenDefined } from 'src/scripts/utils/utils'
import { BodyElement } from 'src/scripts/components/body-element/body-element'

const BODY_ELEMENT_SELECTOR = 'body-element'

export class PopupPromo extends ModalComponent {
  storage: PopupStorage
  conditionRuntime?: ConditionRuntime
  variant: PopupVariant
  hasImage: boolean
  hideImgOnMobile: boolean

  constructor() {
    super()
    this.storage = new PopupStorage(this.dataset.blockId ?? this.id)
    this.variant = this.dataset.variant as PopupVariant
    this.hasImage = this.dataset.hasImage === 'true'
    this.hideImgOnMobile = this.dataset.hideImgMobile === 'true'
  }

  mountComponent(): void {
    if (this.isEditor) {
      this.editor.on('BLOCK_SELECT', this._handleBlockSelect)
      this.editor.on('BLOCK_DESELECT', this._handleBlockDeselect)
      return
    }

    const data = this.storage.loadData()

    if (!data || data.type === PopupType.Promo) {
      if (data?.lastShow) {
        try {
          data.lastShow = new Date(data.lastShow)
        } catch {
          data.lastShow = null
        }
      }

      this.conditionRuntime = new ConditionRuntime(
        this.dataset,
        data?.lastShow ?? null,
        this.show,
      )
      this.conditionRuntime.run()
    }
  }

  unmountComponent(): void {
    this.conditionRuntime?.cleanup()
  }

  _handleBlockSelect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.show()
    }
  }

  _handleBlockDeselect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.hide()
    }
  }

  show = async (): Promise<void> => {
    if (this.variant === 'lightbox') {
      const withGradient = !!this.dataset.overlayGradient
      const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

      const background = withGradient
        ? gradientOpacify(
            this.dataset.overlayGradient,
            this.dataset.overlayOpacity,
          )
        : `color-mix(in srgb, ${this.dataset.overlaySolid} ${
            Number(this.dataset.overlayOpacity) * 100
          }%, transparent)`

      const blurFilter = `blur(${this.dataset.overlayBlur}px)`

      whenDefined('body-element').then(() => {
        bodyElement.showOverlay(this.id, {
          zIndex: 15,
          background,
          backdropFilter: blurFilter,
          '-webkit-backdrop-filter': blurFilter,
          transitionDuration: `${POPUP_ANIMATION_DELAY}ms`,
        })
      })
    }

    await super.show()

    if (this.variant === 'lightbox') {
      this.classList.add(POPUP_CLASSNAMES.lightboxActive)
    }

    if (this.hasImage && !(this.hideImgOnMobile && isMobile())) {
      setTimeout(() => {
        this.classList.add(POPUP_CLASSNAMES.showImage)
      }, POPUP_ANIMATION_DELAY)
    }

    const closeBtn = this.selectBtn('close-small')
    if (closeBtn) {
      closeBtn.style.display = 'flex'
    }

    this.addListener(this.selectBtn('close'), 'click', this.hide)
    this.addListener(this.selectBtn('close-small'), 'click', this.hide)
    this.addListener(this.selectBtn('promo'), 'click', this.onTouch)
  }

  hide = async (): Promise<void> => {
    this.onTouch()

    if (this.variant === 'lightbox') {
      const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)
      whenDefined('body-element').then(() => {
        bodyElement.hideOverlay(this.id)
      })
    }

    this.conditionRuntime?.cleanup()

    this.removeListener(this.selectBtn('close'), 'click', this.hide)
    this.removeListener(this.selectBtn('close-small'), 'click', this.hide)
    this.removeListener(this.selectBtn('promo'), 'click', this.onTouch)

    if (this.variant === 'lightbox') {
      this.classList.remove(POPUP_CLASSNAMES.lightboxActive)
      this.classList.add(POPUP_CLASSNAMES.lightboxClosing)
    }

    await super.hide()
    this.classList.remove(POPUP_CLASSNAMES.showImage)
    this.classList.remove(POPUP_CLASSNAMES.lightboxClosing)
  }

  onTouch = (): void => {
    this.storage.saveData({
      type: PopupType.Promo,
      lastShow: new Date(),
    })
  }

  selectBtn(type: 'promo' | 'close' | 'close-small'): Nullable<HTMLElement> {
    if (!this.isOpen) {
      return null
    }

    return $el(`[data-${type}-btn]`, this)
  }
}
